import index from '../objects/global/index.js';
import initRebuyVariants from '../objects/rebuy/rebuy-widgets.js';

// Global storefront helpers — compiled by webpack to `assets/global.autojs.js`
// and loaded site-wide from layout/theme.liquid, so anything attached to
// `window` here is callable on any page.

// Shopify App Proxy base path. Requests to `${PROXY_BASE_PATH}/api/...` are
// forwarded to the backend, and Shopify appends a signed `logged_in_customer_id`
// for logged-in shoppers — that's how the backend identifies the customer.
const PROXY_BASE_PATH = '/apps/mr-middleware';

/**
 * Add a tag to the currently logged-in customer (idempotent, fire-and-forget).
 *
 * No-ops (without a network call) when there is no logged-in customer or the
 * customer already has the tag — the tag list is read from `window.UBCustomer`
 * (set in theme.liquid). Otherwise POSTs the tag to the backend, which reads the
 * customer id from the *signed* App Proxy params and applies the tag via the
 * Admin API. On success the tag is pushed into `window.UBCustomer.tags` so
 * repeat calls in the same session stay cheap.
 *
 * @param {string} tag
 * @returns {Promise<{added: boolean, reason?: string}>}
 */
async function addCustomerTag(tag) {
  const value = (tag == null ? '' : String(tag)).trim();
  if (!value) return { added: false, reason: 'empty-tag' };

  const customer = window.UBCustomer;
  if (!customer || !customer.id)
    return { added: false, reason: 'not-logged-in' };

  const lower = value.toLowerCase();
  const tags = Array.isArray(customer.tags) ? customer.tags : [];

  // Namespaced tags (e.g. "compatibility:nespresso") are single-valued: a new
  // value replaces any existing tag with the same "namespace:" prefix. The
  // backend does the authoritative add/remove; here we mirror it for the local
  // cache and to decide whether the request is even needed.
  const colon = value.indexOf(':');
  const namespace = colon !== -1 ? lower.slice(0, colon + 1) : null;

  const already = tags.some((t) => String(t).toLowerCase() === lower);
  const stale = namespace
    ? tags.filter((t) => {
        const tl = String(t).toLowerCase();
        return tl.startsWith(namespace) && tl !== lower;
      })
    : [];

  // Nothing to do only when the exact tag is present and no stale same-namespace
  // tag needs clearing.
  if (already && stale.length === 0) {
    return { added: false, reason: 'already-tagged' };
  }

  try {
    const res = await fetch(`${PROXY_BASE_PATH}/api/customer_tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tag: value }),
    });
    if (!res.ok) {
      return { added: false, reason: `http-${res.status}` };
    }
    // Mirror the backend change locally: drop stale same-namespace tags, add the
    // new value, so subsequent calls short-circuit correctly.
    let next = tags.filter((t) => !stale.includes(t));
    if (!already) next = next.concat(value);
    customer.tags = next;
    return { added: true };
  } catch {
    return { added: false, reason: 'network-error' };
  }
}

window.addCustomerTag = addCustomerTag;

// Run all site-wide modules (header, footer, carousels, reveals, etc.) once the
// DOM is ready. `index` is the restructured module barrel (objects/global/index.js,
// merged in from master) — a side-effect import graph of every global module.
document.addEventListener('DOMContentLoaded', () => {
  index();
  initRebuyVariants();
});
